import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Box, Button as MuiButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Form, message, Spin } from 'antd';
import { AutoIcon } from 'assets/icons/common';
import { useEffect, useMemo, useState } from 'react';
import SectionCard from 'components/section/SectionCard';
import { useLocation, useNavigate } from 'react-router-dom';
import AntdValuePreview from 'components/dev/AntdValuePreview';
import { useGetQueryV3DeltaQuery } from 'store/features/api/graphSlice';
import LeaseV9NewTrailModeSchedule from './LeaseV9NewTrailModeSchedule';
import LeaseV9NewTrailModeBulkUpload from './LeaseV9NewTrailModeBulkUpload';
import LeaseV9NewTrailModeDetailsForm from './LeaseV9NewTrailModeDetailsForm';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import IdentityCaseActivityModal from 'pages/IdentityCase/IdentityCaseActivityModal';
import constantFormValues from '../../lease-record-management-v9-new-version/utils/formInputNames';
import {
	isSequential,
	stateUpdater,
	utcConverter,
	isGrossRentLessThanAdvanceOnEachItem,
} from '../../lease-record-management-v9-new-version/utils/helperFunc';

dayjs.extend(utc);

export const useControlData = () => {
	const companyCode = useSelector((state) => state.profile.profileData.creatorOrganizationId);

	const {
		data: getVendorList,
		isLoading: isVendorLoading,
		isError: isVendorError,
	} = useGetQueryV3DeltaQuery(
		{
			companyCode,
			fieldCodes: ['companyCode'],
			fieldValues: [companyCode],
			name: 'getVendorData',
		},
		{
			skip: !companyCode,
		}
	);

	const lynFormProperties = useMemo(
		() => ({
			vendorList:
				getVendorList?.results?.map((item) => ({
					label: item?.vendorName,
					value: item?._id,
					code: item?.vendorCode,
				})) ?? [],
		}),
		[getVendorList]
	);

	return {
		lynFormProperties,
		isLoading: isVendorLoading,
		isError: isVendorError,
	};
};

const LeaseTrailModeV9NewCreate = () => {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const { state, pathname } = useLocation();
	const pinStorageKey = `lease-test-bed-v9-create:pinned:${pathname}`;

	const [selectedTab, setSelectedTab] = useState(0);
	const [selectHistory, setSelectHistory] = useState(null);
	const [isHeaderPinned, setIsHeaderPinned] = useState(() => sessionStorage.getItem(pinStorageKey) !== '0');
	const [formState, setFormState] = useState({
		submitPayload: null,
		showFormSubmitModal: false,
		showActiveDeactiveModal: false,
		modalData: {
			head: state?.processName ?? '',
			label: 'Do you want to proceed?',
		},
	});

	const { lynFormProperties } = useControlData();

	const updateFormState = stateUpdater(setFormState);

	const setShowFormSubmitModal = (value) => {
		updateFormState('showFormSubmitModal', value);
	};

	const [creationApi, { isLoading: isCreateLoading, error: createError, isSuccess: isCreateSuccess, isError: isCreateError }] =
		useGetSingleIdentityMutation();

	useEffect(() => {
		form.resetFields();

		setTimeout(() => {
			if (initialFormValues && form) {
				form.setFieldsValue(initialFormValues);
			}
		}, 0);
	}, [form]);

	useEffect(() => {
		if (isCreateSuccess) {
			toast.success('Success! Action completed ', toastConfig);

			navigate(-1);
		}

		if (isCreateError) {
			if (createError?.data?.code === '501') {
				toast.error(createError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isCreateSuccess, createError, isCreateError]);

	const initialFormValues = useMemo(
		() => ({
			tabIndex: '1',
			version: '',
			leaseId: '',
			leaseTitle: '',
			leaseType: 'Finance Lease',
			lessorName: '',
			lessorID: null,
			leaseTerm: '',
			leaseEndDate: '',
			creationDate: dayjs(),
			lastModifiedDate: dayjs(),
			escalationPercentage: '0',
			inputChangeMonth: '1',
			grossRentList: [
				{
					from: 1,
					to: '',
					amount: '',
					remarks: '',
				},
			],
			grossRentListSet: [[]],
			advanceAmountListSet: [[]],

			advanceAmountList: [
				{
					from: 1,
					to: '',
					amount: '',
					totalAmount: '',
				},
			],
			advanceAmount: '0',
			advanceDuration: '0',
			upfrontLeasePayment: '',
			initialDirectCost: '',
			securityDeposit: '',
			initialDiscountRate: '0',
			identityId: '',
			cashIncentiveAmount: '',

			paymentModality: 'Start',
			paymentFrequency: 'Monthly',
			endOfFinancialYear: utcConverter(dayjs().month(11).date(31)),
			changes: [
				{
					inputChangeEffectiveDate: '',
					inputChangeAdvance: '',
					inputChangeAdvanceAmounts: [],
					inputChangeDescription: 'Initial Rent',
					inputChangeDiscountRate: 0,
					inputChangeEscalation: 0,
					inputChangeGrossAmount: 0,
					inputChangeGrossRents: [],
					inputChangeLeaseTerm: '',
					paymentFrequency: 'Monthly',
					paymentTime: 'Start',
				},
			],
			agreementFiles: [
				{
					id: '',
					name: '',
					file: '',
				},
			],
		}),
		[]
	);

	const handleSubmit = async () => {
		try {
			await form.validateFields();

			const formData = form.getFieldsValue(true);

			if (formData?.paymentFrequency === 'Quarterly' && formData?.leaseTerm % 3 !== 0) {
				message.error(
					"Total lease months must be a multiple of 3 for quarterly payments. Please correct the Lease Payment Schedule's To Month field"
				);
				return;
			}
			if (!isSequential(formData?.grossRentList)) {
				message.error('Gross Rent List must be sequential');
				return;
			}
			if (!isSequential(formData?.advanceAmountList)) {
				message.error('Advance Amount List must be sequential');
				return;
			}
			let grossRentListSet = [];
			let advanceAmountListSet = [];
			let effectiveDateHistory = [];

			grossRentListSet?.push(formData?.grossRentList);

			advanceAmountListSet?.push(formData?.advanceAmountList);
			effectiveDateHistory?.push(utcConverter(formData?.leaseStartDate));

			const isGrossRentAndAdvanceAmountValid = isGrossRentLessThanAdvanceOnEachItem(formData?.grossRents, formData?.advanceAmounts);

			if (!isGrossRentAndAdvanceAmountValid) {
				message.error('Inconsistency between Lease Payment and Advance Adjustment');
				return;
			}

			const createPagePayload = {
				modificationSequence: 0,
				leaseStartDate: utcConverter(formData?.leaseStartDate),
				leaseTitle: formData?.leaseTitle,
				lessorName: formData?.lessorName,
				lessorID: formData?.lessorID,
				leaseTerm: formData?.leaseTerm,
				leaseEndDate: utcConverter(formData?.leaseEndDate),
				paymentModality: formData?.paymentModality,
				leaseType: formData?.leaseType,
				initialDiscountRate: formData?.initialDiscountRate,
				grossRentList: formData?.grossRentList,
				paymentFrequency: formData?.paymentFrequency,
				advanceAmountList: formData?.advanceAmountList,
				advanceAmount: formData?.advanceAmount,
				advanceAmounts: formData?.advanceAmounts,
				upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
				initialDirectCost: formData?.initialDirectCost ?? 0,
				securityDeposit: formData?.securityDeposit ?? 0,
				grossRents: formData?.grossRents,
				cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
				grossRent: 0,
				escalationPercentage: 0,
				grossRentListSet,
				advanceAmountListSet,
				effectiveDateHistory,
				modifyCount: 0,
				editCount: 0,
				changes: [
					{
						paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
						paymentTime: formData?.changes?.[0]?.paymentTime,
						inputChangeLeaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
						inputChangeGrossAmount: formData?.changes?.[0]?.inputChangeGrossAmount ?? 0,
						inputChangeDiscountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
						inputChangeEscalation: formData?.changes?.[0]?.inputChangeEscalation ?? 0,
						inputChangeAdvance: formData?.changes?.[0]?.inputChangeAdvance,
						inputChangeEffectiveDate: formData?.changes?.[0]?.inputChangeEffectiveDate,
						inputChangeAdvanceAmounts: formData?.advanceAmounts,
						inputChangeGrossRents: formData?.grossRents,
						inputChangeDescription: formData?.changes?.[0]?.inputChangeDescription,
						upfrontLeasePayment: formData?.upfrontLeasePayment ?? 0,
						initialDirectCost: formData?.initialDirectCost ?? 0,
						securityDeposit: formData?.securityDeposit ?? 0,
						cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					},
				],
				modifiedDateTime: utcConverter(dayjs()),
				paymentModalityLabel: constantFormValues?.paymentModality?.options?.find((item) => item.value === formData?.paymentModality)?.label,

				lastModifiedDate: utcConverter(dayjs()),

				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0).length,
			};

			updateFormState('showFormSubmitModal', true);
			updateFormState('modalData.head', 'Confirmation');
			updateFormState('modalData.label', 'Do you want to create?');

			updateFormState('submitPayload', createPagePayload);
		} catch (error) {
			console.error('Validation Failed:', error);
			toast.error('Please fill all required fields', toastConfig);
		}
	};

	const handleModalSubmit = async () => {
		await creationApi({
			operation: 'add-single',
			processCode: state?.processCode,
			suffixCode: '',
			data: formState?.submitPayload,
		});
	};

	const selectTabHandler = (_, newValue) => {
		setSelectedTab(newValue);
	};

	const handleAutoPinToggle = () => {
		setIsHeaderPinned((previousValue) => {
			const nextValue = !previousValue;
			sessionStorage.setItem(pinStorageKey, nextValue ? '1' : '0');
			return nextValue;
		});
	};

	const showSpinner = isCreateLoading;

	const tabItem = [
		{
			key: '1',
			label: 'Lease Details',
			children: <LeaseV9NewTrailModeDetailsForm handleSubmit={handleSubmit} vendorList={lynFormProperties?.vendorList} />,
		},
		{
			key: '2',
			label: 'Lease Schedule',
			children: (
				<SectionCard>
					<LeaseV9NewTrailModeSchedule form={form} />
				</SectionCard>
			),
		},
		{
			key: '3',
			label: 'Bulk Upload',
			children: (
				<SectionCard>
					<LeaseV9NewTrailModeBulkUpload vendorList={lynFormProperties?.vendorList} />
				</SectionCard>
			),
		},
	];

	return (
		<Box>
			<Spin spinning={showSpinner} style={{ maxHeight: '100%' }}>
				<Box
					sx={{
						position: isHeaderPinned ? 'sticky' : 'relative',
						top: isHeaderPinned ? 0 : 'auto',
						zIndex: 10,
						mb: 2,
						border: '1px solid #e6ebf2',
						borderRadius: '10px',
						backgroundColor: '#fff',
						boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
						overflow: 'hidden',
					}}
				>
					<Stack
						direction={{ xs: 'column', md: 'row' }}
						alignItems={{ xs: 'flex-start', md: 'center' }}
						justifyContent="space-between"
						spacing={1.5}
						sx={{ px: { xs: 1.5, md: 2 }, py: 1.5 }}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography
								sx={{
									fontSize: '18px',
									fontWeight: 600,
									color: '#1f2937',
									lineHeight: 1.2,
								}}
							>
								{`Create ${state?.processName}`}
							</Typography>
						</Box>

						<Tooltip title={isHeaderPinned ? 'Pinned' : 'Unpin'}>
							<MuiButton
								onClick={handleAutoPinToggle}
								aria-label={isHeaderPinned ? 'Unpin header' : 'Pin header'}
								sx={{
									minWidth: '36px',
									width: '36px',
									height: '36px',
									borderRadius: '10px',
									color: isHeaderPinned ? '#1D4ED8' : '#374151',
									border: `1px solid ${isHeaderPinned ? '#BFDBFE' : '#E5E7EB'}`,
									backgroundColor: isHeaderPinned ? '#EFF6FF' : '#FFFFFF',
								}}
							>
								<AutoIcon />
							</MuiButton>
						</Tooltip>
					</Stack>

					<Box
						sx={{
							px: { xs: 0.25, md: 0.5 },
							borderTop: '1px solid #eef2f7',
						}}
					>
						<Tabs
							value={selectedTab}
							onChange={selectTabHandler}
							variant="scrollable"
							scrollButtons="auto"
							allowScrollButtonsMobile
							sx={{
								minHeight: '36px',
								'& .MuiTabs-indicator': {
									height: '2px',
									backgroundColor: '#2563eb',
									borderRadius: '999px',
								},
								'& .MuiTab-root': {
									minHeight: '36px',
									px: { xs: 1.25, md: 1.5 },
									mr: { xs: 0.5, md: 1 },
									textTransform: 'none',
									fontSize: '14px',
									fontWeight: 500,
									color: '#667085',
								},
								'& .Mui-selected': {
									color: '#2563eb !important',
									fontWeight: 600,
								},
							}}
						>
							{tabItem?.map((item) => (
								<Tab key={item.key} label={item.label} />
							))}
						</Tabs>
					</Box>
				</Box>

				<Form
					form={form}
					layout="vertical"
					autoComplete="off"
					requiredMark={false}
					onFinish={handleSubmit}
					initialValues={initialFormValues}
				>
					<AntdValuePreview form={form} show={false} />

					<>
						{tabItem?.map((item, index) => (
							<Box
								key={index}
								style={{
									display: selectedTab === index ? 'block' : 'none',
								}}
							>
								{item.children}
							</Box>
						))}
					</>
				</Form>

				{formState?.showFormSubmitModal && (
					<ComptrollerConfirmModal
						modalData={formState?.modalData}
						handleSubmit={handleModalSubmit}
						setShowModal={setShowFormSubmitModal}
						showModal={formState?.showFormSubmitModal}
					/>
				)}

				{selectHistory && (
					<IdentityCaseActivityModal title={state?.processName} selectItem={selectHistory} setSelectHistory={setSelectHistory} />
				)}
			</Spin>
		</Box>
	);
};

export default LeaseTrailModeV9NewCreate;
